import { InspectSkill } from '@sb/ai/types';
import type { Step as RcStep } from '@sb/remote-control/types';
import { intensiveAIFeaturesAllowed } from '@sb/service-interfaces/host/processorGate';

import {
  type RoutineView,
  type ValidationContext,
  type ValidationRule,
} from '../../types';
import { resolveCameraEquipmentReference } from '../../util/cameraEquipment';
import { ActionRequiredError } from '../../util/errors';
import {
  assertNotExamplePlaceholder,
  SKILL_PLACEHOLDER_MESSAGE,
} from '../../util/examplePlaceholder';
import { humanJoin } from '../../util/humanJoin';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';
import {
  checkCaptureDestination,
  checkCaptureFileName,
  checkCaptureFilepath,
} from './captureConfigChecks';

type InspectArgs = Extract<
  RcStep.Arguments,
  { argumentKind: 'RunInspectSkill' }
>;

const NO_INSPECT_SKILL_MESSAGE = 'No Inspect skill selected';

/**
 * Blocks an Inspect step whose skill is unselected, unparseable, or no longer
 * describes the classes the step was configured against.
 *
 * Deliberately NOT checking two things the plan expected. A classifier and a
 * multi-output workflow are both rejected at IMPORT, in the wizard, and a saved
 * skill records neither `project_task_type` nor the output list — by the time a
 * skill exists, `roboflowOutputName` is a required field that was resolved
 * against the workflow's schema. Re-deriving those here would need a cloud call
 * from a pre-flight check.
 */
class RunInspectSkillRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.RunInspectSkill', 'RunInspectSkill');
  }

  protected validate({
    args,
    ctx,
    routine,
    warn,
  }: CanonicalValidateArgs): void {
    const a = args as InspectArgs;

    if (!a.skillId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: NO_INSPECT_SKILL_MESSAGE,
        fieldId: 'skillId',
      });
    }

    // Before the lookup, which skips a skill it cannot find.
    assertNotExamplePlaceholder(
      a.skillId,
      'skillId',
      SKILL_PLACEHOLDER_MESSAGE,
    );

    this.checkProcessor(ctx);

    this.checkCaptureConfig(a, routine, ctx);

    // Skips only when the caller supplied no skills; a supplied list without
    // this one means it was deleted or is another kind.
    const skill = this.resolveSkill(
      ctx,
      a.skillId,
      'inspect',
      NO_INSPECT_SKILL_MESSAGE,
    );

    if (!skill) return;

    warn(this.draftSkillWarning(skill));

    const parsed = InspectSkill.safeParse(skill);

    if (!parsed.success) {
      const invalid = [
        ...new Set(parsed.error.issues.map((issue) => issue.path.join('.'))),
      ];

      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `This Inspect skill has invalid saved configuration (${humanJoin(
          invalid,
        )}) and cannot run. Reopen the skill and re-enter those values.`,
        fieldId: 'skillId',
      });
    }

    // The step caches the skill's class list so the variable picker can offer
    // count_<id> before the step has ever run. A class the skill no longer has
    // means the picker is offering — and a saved `If` may already reference — a
    // variable no run will publish, which reads at run time as the defect never
    // being found rather than as a condition pointing at nothing.
    //
    // Blocking rather than warning, for the same reason `steps/Teleop` fails
    // closed: the failure is silent at run time and the fix is cheap. Opening
    // the step refreshes the cache from the skill.
    const liveIds = new Set(parsed.data.shared.classes.map(({ id }) => id));

    // `?? []` because stored args reach a rule as they were written, not
    // zod-defaulted: a step authored through the DSL (whose example passes
    // `skillId` alone) or saved by a build predating the field has no `classes`
    // at all. An empty cache has nothing stale in it — opening the step fills
    // it from the skill.
    const stale = (a.classes ?? [])
      .filter(({ id }) => !liveIds.has(id))
      .map(({ label }) => label);

    if (stale.length > 0) {
      const staleNoun = stale.length === 1 ? 'that class' : 'those classes';

      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `This Inspect skill no longer has ${humanJoin(
          stale,
        )}. Reopen the step to pick up the skill's current classes, and check any condition written against ${staleNoun}.`,
        fieldId: 'skillId',
      });
    }

    this.checkCamera(parsed.data.shared.cameraId, ctx);
  }

  /**
   * The camera the skill captures through has to still be a camera this robot
   * has. Only the lookup: `cameraId` is `.min(1)` on the schema, so an unset
   * one never reaches here — the parse above has already refused it. Inspect
   * runs detection on the GPU task runner, not on camera geometry, so no
   * calibration is required and the resolved camera is discarded.
   *
   * @throws {ActionRequiredError}
   */
  private checkCamera(cameraId: string, ctx: ValidationContext): void {
    resolveCameraEquipmentReference(ctx, cameraId, {
      subject: 'this Inspect skill',
      openTarget: 'skill',
      fieldId: 'skillId',
    });
  }

  /**
   * Validate the optional `captureConfig` when present.
   *
   * The destination and filename checks are shared with `Locate`, which writes
   * through the same runner code. What is Inspect's own is the equipment
   * lookup below.
   *
   * @throws {ActionRequiredError}
   */
  private checkCaptureConfig(
    args: InspectArgs,
    routine: RoutineView,
    ctx: ValidationContext,
  ): void {
    const { captureConfig } = args;

    if (!captureConfig) return;

    checkCaptureDestination(captureConfig);
    checkCaptureFileName(captureConfig, routine, ctx);

    // A named id has to resolve to something that can take a file. A truthy id
    // satisfies the destination check above while naming a deleted device or a
    // gripper, which is the failure this whole rule exists to move off the run
    // path: the frame is captured, every check passes, and the step fails
    // partway through a routine.
    //
    // Every named id, not just the legacy one — a `destinations` entry points
    // at equipment the same way and reaches the same lookup in the runner.
    const namedEquipmentIds = [
      captureConfig.saveEquipmentId,
      ...(captureConfig.destinations ?? []).map((destination) =>
        destination.kind === 'equipment' ? destination.equipmentId : undefined,
      ),
    ].filter((id): id is string => Boolean(id));

    // Only when the caller loaded equipment at all. `findEquipment` returns
    // undefined for both "no such device" and "this context has no equipment
    // list", and the rule already treats an absent `skills` list as "not loaded
    // here" rather than as a deletion. Checking regardless would turn a
    // caller's context shape into a blocked routine.
    if (Array.isArray(ctx?.equipment)) {
      for (const equipmentId of namedEquipmentIds) {
        const storage = this.findEquipment(ctx, equipmentId);

        if (!storage) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: `Storage equipment not found (${equipmentId})`,
            fieldId: 'captureConfig.saveEquipmentId',
          });
        }

        if (
          storage.config.kind !== 'FTPServer' &&
          storage.config.kind !== 'S3Bucket'
        ) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: 'Storage equipment must be an FTP Server or S3 Bucket',
            fieldId: 'captureConfig.saveEquipmentId',
          });
        }
      }
    }

    checkCaptureFilepath(captureConfig.saveFilepath);
  }

  /**
   * Refuses an Inspect step on a box too weak to run one. Checked before the
   * skill is resolved, so it holds on a caller that did not load `skills`.
   *
   * @throws {ActionRequiredError}
   */
  private checkProcessor(ctx: ValidationContext): void {
    // Omitted means the caller never asked the host; see ValidationContext.
    if (ctx.processorPCores == null) return;

    if (intensiveAIFeaturesAllowed(ctx.processorPCores)) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message:
        'Inspect needs more compute than this control box has, so this step cannot run here.',
      fieldId: 'skillId',
    });
  }
}

export const RunInspectSkillValidation: ValidationRule =
  new RunInspectSkillRule().toValidationRule();
